import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import Button from 'components/Button';
import CustomSearchbar from 'components/CustomSearchBar';
import DeviceList from 'components/DeviceComponents/DeviceList';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import assignDevicesToMemberThunk from 'redux-store/thunks/organizationOverview/assignDevicesToMemberThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AssignDevicesToMembersModalStageProps } from '..';

const AssignDevicesToMembers: FC<AssignDevicesToMembersModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
  onClose,
  organizationId,
  userId,
  selectedMembers,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const organizationName = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.name,
  );

  const organizationMembers = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.users ?? [],
  );

  const organizationDevices = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.devices ?? [],
  );

  const memberCount = useCallback(
    (deviceId: number) =>
      organizationDevices.find(orgDevice => orgDevice.id === deviceId)?.users
        .length,
    [organizationDevices],
  );

  const devicesAssignedToMember = useMemo(
    () =>
      organizationMembers.find(member => member.id === userId)?.devices || [],
    [organizationMembers, userId],
  );

  const unassignedDevices = organizationDevices.filter(
    device => !devicesAssignedToMember.includes(device.id),
  );

  const fullStatus = useAppSelector(appState => appState.api.fullStatus);

  const devicesWithNames = useMemo(() => {
    return unassignedDevices.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [unassignedDevices, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  useEffect(() => {
    const newAddedDevice = unassignedDevices.find(
      device => device.serial === state.newDeviceSerial,
    );

    if (newAddedDevice?.id) {
      setState(prev => ({ ...prev, devices: [newAddedDevice.id] }));
    }
  }, [unassignedDevices, setState, state.newDeviceSerial]);

  const selectedDeviceIds = useMemo(() => state.devices, [state.devices]);
  const setSelectedDeviceIds = useCallback(
    (devices: number[]) => {
      setState(prev => ({ ...prev, devices }));
    },
    [setState],
  );

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  const handleAssignDevicesToMember = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      const membersToAssign = userId ? [userId] : selectedMembers;

      if (!organizationId || membersToAssign.length === 0) {
        return;
      }

      try {
        setLoading(true);

        const results = await Promise.all(
          membersToAssign.map(id =>
            dispatch(
              assignDevicesToMemberThunk({
                organizationId: organizationId,
                userId: id,
                data: {
                  devices: selectedDeviceIds,
                },
              }),
            ).unwrap(),
          ),
        );

        const allSuccess = results.every(result => result.success);

        if (allSuccess) {
          await dispatch(
            getOrganizationByIdThunk({ organizationId: organizationId }),
          ).unwrap();

          setState({ devices: [], stage: 'success' });
          setTimeout(() => {
            setState({ devices: [], stage: 'devices' });
            onClose();
          }, 1250);
        } else {
          setError(
            t(
              'common:pages.organization_overview.assign_devices_to_member.cannot_assign',
            ),
          );
        }
      } catch (e) {
        Sentry.captureException(e, {
          extra: { membersToAssign, selectedDeviceIds },
          tags: { page: 'AssignDevicesToMembersModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      userId,
      selectedMembers,
      organizationId,
      dispatch,
      selectedDeviceIds,
      setState,
      onClose,
      t,
    ],
  );

  return (
    <>
      <Box
        sx={{
          height: '45vh',
          overflow: 'auto',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography
            variant="h2"
            mb={2}
            textAlign="center"
            data-testid="assign-devices-title"
          >
            {t(
              'common:pages.organization_overview.assign_devices_to_member.title',
            )}
          </Typography>
        </Box>
        <Typography
          variant="h3"
          mb={1}
          data-testid="assign-devices-org-devices"
        >
          {t(
            `common:pages.organization_overview.assign_devices_to_member.org_devices`,
            { organization: organizationName },
          )}
        </Typography>
        <Box>
          <Box mb={3} width="100%">
            <CustomSearchbar
              search={searchString}
              setSearch={setSearchString}
              hasSearched={searchString.length > 0}
              handleSearch={handleSearch}
              handleClearSearch={handleClearSearch}
              testId="assign-devices-to-members-search-bar"
            />
          </Box>
          {filteredDevices.length > 0 ? (
            filteredDevices.map(item => (
              <DeviceList
                key={item.id}
                deviceId={item.id}
                selectable
                serial={item.serial}
                setSelectedDeviceIds={setSelectedDeviceIds}
                selectedDeviceIds={selectedDeviceIds}
                rightComponent={
                  memberCount(item.id) ? (
                    <RightComponentWrapper>
                      <GroupOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'grey.500' }}
                      />
                      <Typography
                        data-testid="number-of-members"
                        variant="body1"
                        noWrap
                        color="grey.500"
                      >
                        {memberCount(item.id)}
                      </Typography>
                    </RightComponentWrapper>
                  ) : null
                }
              />
            ))
          ) : (
            <Typography
              variant="body1"
              color="grey.600"
              textAlign="center"
              data-testid="empty-search-text"
            >
              {t('common:pages.organization_overview.add_user.search_empty')}
            </Typography>
          )}
          <ErrorText error={error} />
        </Box>
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          size="large"
          fullWidth
          onClick={onCloseProxy}
          data-testid="modal-cancel-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="flat"
          size="large"
          fullWidth
          disabled={isLoading}
          onClick={handleAssignDevicesToMember}
          data-testid="modal-save-button"
        >
          {t('common:save')}
        </Button>
      </Box>
    </>
  );
};

export default AssignDevicesToMembers;
